import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSessionContext } from './SessionContext';
import { useFitnessContext } from './FitnessContext';
import { useGoalsContext } from './GoalsContext';
import { techniques as baseTechniques } from '../data/techniques';

const GamificationContext = createContext();

export const useGamificationContext = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamificationContext must be used within GamificationProvider');
  }
  return context;
};

export const GamificationProvider = ({ children }) => {
  const { sessions } = useSessionContext();
  const { getStats: getFitnessStats } = useFitnessContext();
  const { overallGoals, sessionGoals } = useGoalsContext();

  // --- Gamification Toggle ---
  const [gamificationEnabled, setGamificationEnabled] = useState(() => {
    const saved = localStorage.getItem('clinchfirst_gamification_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('clinchfirst_gamification_enabled', JSON.stringify(gamificationEnabled));
  }, [gamificationEnabled]);

  // --- XP & Leveling System ---
  const calculateUserLevel = () => {
    let xp = 0;
    
    // XP from Sessions (100xp per session)
    xp += sessions.length * 100;
    
    // XP from Techniques (20xp per usage, +30xp bonus for success)
    sessions.forEach(session => {
      session.techniques?.forEach(t => {
        xp += 20;
        if (t.outcome === 'Succeeded') xp += 30;
      });
    });

    // XP from Goals (200xp per completed session goal, 500xp per overall goal milestone)
    xp += sessionGoals.filter(g => g.completed).length * 200;
    overallGoals.forEach(g => {
      xp += g.milestones.filter(m => m.completed).length * 500;
    });

    // XP from Fitness (50xp per goal reached)
    const fitnessStats = getFitnessStats();
    xp += fitnessStats.goalsReached * 50;

    // Calculate Level (Simple quadratic curve: Level = sqrt(XP/100))
    const level = Math.floor(Math.sqrt(xp / 100)) + 1;
    const currentLevelXp = 100 * Math.pow(level - 1, 2);
    const nextLevelXp = 100 * Math.pow(level, 2);
    const progressToNext = Math.min(100, Math.max(0, ((xp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100));

    return { xp, level, progressToNext, nextLevelXp, currentLevelXp };
  };

  const userStats = useMemo(calculateUserLevel, [sessions, sessionGoals, overallGoals, getFitnessStats]);

  // --- Technique Mastery Logic ---
  const calculateTechniqueMastery = (techniqueName) => {
    // Find all usages
    const usages = [];
    sessions.forEach(session => {
      session.techniques?.forEach(t => {
        if (t.name.toLowerCase() === techniqueName.toLowerCase()) {
          usages.push(t);
        }
      });
    });

    const count = usages.length;
    const successes = usages.filter(u => u.outcome === 'Succeeded').length;
    const successRate = count > 0 ? (successes / count) * 100 : 0;

    // Mastery Levels
    // 0: Novice (0-5 uses)
    // 1: Apprentice (5+ uses)
    // 2: Practitioner (15+ uses, >30% success)
    // 3: Expert (30+ uses, >50% success)
    // 4: Specialist (50+ uses, >70% success) - Renamed from Master
    
    let masteryLevel = 0;
    let masteryTitle = 'Novice';
    let progress = 0;

    if (count >= 50 && successRate >= 70) {
      masteryLevel = 4;
      masteryTitle = 'Specialist';
      progress = 100;
    } else if (count >= 30 && successRate >= 50) {
      masteryLevel = 3;
      masteryTitle = 'Expert';
      progress = Math.min(100, (count / 50) * 100);
    } else if (count >= 15 && successRate >= 30) {
      masteryLevel = 2;
      masteryTitle = 'Practitioner';
      progress = Math.min(100, (count / 30) * 100);
    } else if (count >= 5) {
      masteryLevel = 1;
      masteryTitle = 'Apprentice';
      progress = Math.min(100, (count / 15) * 100);
    } else {
      masteryLevel = 0;
      masteryTitle = 'Novice';
      progress = Math.min(100, (count / 5) * 100);
    }

    return { level: masteryLevel, title: masteryTitle, progress, count, successRate };
  };

  // --- Achievements System ---
  const achievementDefinitions = [
    {
      id: 'first_session',
      title: 'First Step',
      description: 'Log your first training session',
      icon: 'FiFlag',
      check: () => sessions.length >= 1
    },
    {
      id: 'dedicated',
      title: 'Dedicated',
      description: 'Log 10 training sessions',
      icon: 'FiZap',
      check: () => sessions.length >= 10
    },
    {
      id: 'iron_man',
      title: 'Iron Man',
      description: 'Log 50 training sessions',
      icon: 'FiAward',
      check: () => sessions.length >= 50
    },
    {
      id: 'hydrated',
      title: 'Hydro Homie',
      description: 'Reach a 7-day hydration streak',
      icon: 'FiDroplet',
      check: () => getFitnessStats().currentStreak >= 7
    },
    {
      id: 'technician',
      title: 'Technician',
      description: 'Reach Expert level in a technique (Level 3+)',
      icon: 'FiTarget',
      check: () => baseTechniques.some(t => calculateTechniqueMastery(t.name).level >= 3)
    },
    {
      id: 'goal_getter',
      title: 'Goal Getter',
      description: 'Complete 5 Session Goals',
      icon: 'FiCheckCircle',
      check: () => sessionGoals.filter(g => g.completed).length >= 5
    }
  ];

  const unlockedAchievements = useMemo(() => {
    return achievementDefinitions.map(ach => ({
      ...ach,
      unlocked: ach.check(),
      unlockedAt: ach.check() ? new Date().toISOString() : null // In a real app, store simple timestamp
    }));
  }, [sessions, sessionGoals, getFitnessStats]);

  const recentUnlocks = unlockedAchievements.filter(a => a.unlocked).slice(-3);

  return (
    <GamificationContext.Provider value={{
      gamificationEnabled,
      setGamificationEnabled,
      userStats,
      calculateTechniqueMastery,
      unlockedAchievements,
      achievementDefinitions,
      recentUnlocks
    }}>
      {children}
    </GamificationContext.Provider>
  );
};